import React, { useState, useEffect } from "react";
import Grid from "../Grid/Grid";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
  Stack,
  Autocomplete,
  TextField,
  Button,
  FormControlLabel,
  Switch
} from "@mui/material";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import transactionAPI from "../../Network/Transaction";
import mizanListAPI from "../../Network/MizanList";
import { selectRoles } from "../../reduxStore/RolesReducer";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CustomDateRange from "../../Components/CustomDateRange";
import { SiMicrosoftexcel } from 'react-icons/si';
import { BsFilePdfFill } from 'react-icons/bs';

const cookies = new Cookies();

const todayStart = new Date();
todayStart.setHours(0, 0, 0, 0);
const todayEnd = new Date();
todayEnd.setHours(23, 59, 59, 999);

const yesterday = new Date(todayStart);
yesterday.setDate(yesterday.getDate() - 1);

export default function AccountStatement() {
  const [t] = useTranslation("common");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [mizanList, setMizanList] = useState([]);
  const [loadingMizan, setLoadingMizan] = useState(false);
  const [summary, setSummary] = useState(null);

  const [filters, setFilters] = useState({
    mizan: null,
    mizan_id: "",
    creation_date_from: moment().subtract(1, "month").startOf("day").format('YYYY-MM-DD') + " 00:00:00",
    creation_date_to: moment().endOf('day').format('YYYY-MM-DD') + " 23:59:59",
    creationDateFrom: moment().subtract(1, "month").startOf("day").toDate(),
    creationDateTo: todayEnd,
    all_time: 0,
    useLastAdjustment: false
  });

  const [reload, setReload] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rolesRedux = useSelector(selectRoles);

  const handlePageChange = (newPage) => {
    setPageNumber(newPage + 1);
  };

  const columns = [
    { field: "index_column", headerName: "ت", minWidth: 50, maxWidth: 60 },
    { field: "formatted_date", headerName: t("GLOBAL.date"), minWidth: 100, flex: 1 },
    { field: "formatted_time", headerName: t("GLOBAL.time"), minWidth: 100, flex: 1 },
    { field: "transaction_number", headerName: t("TRANSACTIONS.transaction_number"), minWidth: 120, flex: 1.2 },
    { field: "unit_registration_number", headerName: t("TRANSACTIONS.unit"), minWidth: 120, flex: 1 },
    { 
      field: "internal", 
      headerName: t("TRANSACTIONS.total_internal_weight") || "داخلي (طن)", 
      minWidth: 100, 
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ color: 'green', fontWeight: 'bold' }}>
          {params.value > 0 ? params.value?.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : ""}
        </Typography>
      )
    },
    { 
      field: "external", 
      headerName: t("TRANSACTIONS.total_external_weight") || "خارجي (طن)", 
      minWidth: 100, 
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ color: 'red', fontWeight: 'bold' }}>
          {params.value > 0 ? params.value?.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : ""}
        </Typography>
      )
    },
    { 
      field: "settlement", 
      headerName: t("TRANSACTIONS.total_adjustment_weight") || "تسوية (طن)", 
      minWidth: 100, 
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ color: 'orange', fontWeight: 'bold' }}>
          {params.value !== 0 ? params.value?.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : ""}
        </Typography>
      )
    },
    { 
      field: "running_balance", 
      headerName: t("TRANSACTIONS.running_balance") || "الرصيد التراكمي", 
      minWidth: 130, 
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 'bold' }}>
          {params.value?.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
        </Typography>
      )
    }
  ];

  const headerObject = {
    username: cookies.get("user_name"),
    user_id: cookies.get("user_id"),
    account_name: cookies.get("account_name"),
    account_id: cookies.get("account_id"),
    authHash: cookies.get("authHash"),
    sid: cookies.get("sid"),
    baseurl: cookies.get("baseUrl"),
    id: cookies.get("id"),
  };

  const getMizans = async () => {
    setLoadingMizan(true);
    try {
      const result = await mizanListAPI.getMizanList({ params: { ...headerObject } });
      if (result.status && result?.data?.data) {
        setMizanList(result.data.data.data || []);
      }
    } catch (error) {
      console.error(error);
    }
    setLoadingMizan(false);
  };

  const loadData = async (exportParams = {}) => {
    if (!filters.mizan_id) {
      toast.info(t("MESSAGES.select_mizan") || "يرجى اختيار الميزان أولاً");
      return;
    }

    const isExport = exportParams.export_excel || exportParams.export_pdf;
    if (!isExport) setLoading(true);
    else setExportLoading(true);

    try {
      const result = await transactionAPI.mizanAccountStatement({
        params: {
          ...headerObject,
          mizan_id: filters.mizan_id,
          creation_date_from: filters.creation_date_from,
          creation_date_to: filters.creation_date_to,
          use_last_adjustment: filters.useLastAdjustment ? 1 : 0,
          ...exportParams
        }
      });

      if (result.status && result?.data?.data) {
        if (exportParams.export_excel) {
          handleDownloadExcel(result.data.data);
        } else if (exportParams.export_pdf) {
          handleDownloadPdf(result.data.data);
        } else {
          const ledger = (result.data.data.ledger || []).map(row => ({
            ...row,
            internal: row.internal / 1000,
            external: row.external / 1000,
            settlement: row.settlement / 1000,
            running_balance: row.running_balance / 1000
          }));
          setRows(ledger);
          const summaryData = result.data.data.summary || null;
          if (summaryData) {
            setSummary({
              opening_balance: summaryData.opening_balance / 1000,
              total_external: summaryData.total_external / 1000,
              total_internal: summaryData.total_internal / 1000,
              total_adjustment: summaryData.total_adjustment / 1000,
              net_movement: summaryData.net_movement / 1000,
              closing_balance: summaryData.closing_balance / 1000
            });
          } else {
            setSummary(null);
          }
          setPageCount(Math.ceil(ledger.length / pageSize));
        }
      } else {
        if (!isExport) {
          setRows([]);
          setSummary(null);
          setPageCount(0);
        }
        toast.error(result?.data?.data?.message || t("NETWORK_MESSAGES.unknownError"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    }
    setLoading(false);
    setExportLoading(false);
  };

  const handleDownloadExcel = async (fileName) => {
    const result = await transactionAPI.downloadExcel({
      params: { ...headerObject, fileName }
    });
    if (result.status && result?.data) {
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${t("SIDEMENU.account_statement")}_${filters.mizan?.display_name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleDownloadPdf = async (fileName) => {
    const result = await transactionAPI.downloadPdf({
      params: { ...headerObject, fileName }
    });
    if (result.status && result?.data) {
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${t("SIDEMENU.account_statement")}_${filters.mizan?.display_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    getMizans();
  }, []);

  useEffect(() => {
    if (filters.mizan_id) {
      loadData();
    }
  }, [reload, filters.mizan_id, pageNumber, pageSize]);

  const SummaryCard = ({ title, value, color }) => (
    <Card sx={{ minWidth: 150, borderTop: `4px solid ${color}`, flex: 1 }}>
      <CardContent>
        <Typography color="textSecondary" gutterBottom noWrap sx={{ fontFamily: 'Cairo-Bold', fontSize: '0.8rem' }}>
          {title}
        </Typography>
        <Typography variant="h6" component="div" sx={{ fontFamily: 'Cairo-Bold', color: color }}>
          {value?.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) || 0}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 2, overflowY: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" sx={{ fontFamily: 'Cairo-Bold' }}>
          {t("SIDEMENU.account_statement") || "كشف حركات الميزان"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t("GLOBAL.export_excel")}>
            <IconButton 
              color="success" 
              onClick={() => loadData({ export_excel: true })}
              disabled={!rows.length || exportLoading}
            >
              <SiMicrosoftexcel />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("GLOBAL.export_pdf")}>
            <IconButton 
              color="error" 
              onClick={() => loadData({ export_pdf: true })}
              disabled={!rows.length || exportLoading}
            >
              <BsFilePdfFill />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Autocomplete
            sx={{ 
              minWidth: 300, 
              flex: 1,
              "& .MuiAutocomplete-input": { fontFamily: 'Cairo' },
              "& .MuiInputLabel-root": { fontFamily: 'Cairo' }
            }}
            options={mizanList}
            loading={loadingMizan}
            getOptionLabel={(option) => option.display_name || ""}
            value={filters.mizan}
            onChange={(e, v) => setFilters({ ...filters, mizan: v, mizan_id: v?.id || "" })}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t("GLOBAL.mizan") || "الميزان"} 
                variant="outlined" 
                size="small" 
                sx={{ 
                  "& .MuiInputBase-root": { fontFamily: 'Cairo' },
                  "& .MuiInputLabel-root": { fontFamily: 'Cairo' }
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ fontFamily: 'Cairo' }}>
                {option.display_name}
              </Box>
            )}
          />

          <FormControlLabel
            control={
              <Switch
                checked={filters.useLastAdjustment}
                onChange={(e) => setFilters({ ...filters, useLastAdjustment: e.target.checked })}
              />
            }
            label={<Typography sx={{ fontFamily: 'Cairo', fontSize: '13px' }}>من اخر تصفير</Typography>}
          />

          <CustomDateRange
            dateRange={filters}
            setDateRange={(v) => setFilters({ ...filters, ...v })}
            horizontal={true}
            showAllTime={false}
            disabledFrom={filters.useLastAdjustment}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => setReload(!reload)}
              sx={{ fontFamily: 'Cairo-Bold' }}
            >
              {t("GLOBAL.search")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => {
                
                setFilters({
                  ...filters,
                  mizan: null,
                  mizan_id: "",
                  creation_date_from: moment().subtract(1, "month").startOf("day").format('YYYY-MM-DD') + " 00:00:00",
                  creation_date_to: moment().endOf('day').format('YYYY-MM-DD') + " 23:59:59",
                  creationDateFrom: moment().subtract(1, "month").startOf("day").toDate(),
                  creationDateTo: todayEnd,
                });
                setRows([]);
                setSummary(null);
                setPageCount(0);
              }}
              sx={{ fontFamily: 'Cairo-Bold' }}
            >
              {t("GLOBAL.clear")}
            </Button>
          </Stack>
        </Stack>
      </Card>

      {summary && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <SummaryCard title={t("GLOBAL.opening_balance")} value={summary.opening_balance} color="#757575" />
          <SummaryCard title={t("GLOBAL.total_external")} value={summary.total_external} color="red" />
          <SummaryCard title={t("GLOBAL.total_internal")} value={summary.total_internal} color="green" />
          <SummaryCard title={t("GLOBAL.total_adjustment")} value={summary.total_adjustment} color="orange" />
          <SummaryCard title={t("GLOBAL.net_movement")} value={summary.net_movement} color={summary.net_movement >= 0 ? "green" : "red"} />
          <SummaryCard title={t("GLOBAL.closing_balance")} value={summary.closing_balance} color="#1976d2" />
        </Stack>
      )}

      <Box sx={{ flex: 1, minHeight: 400 }}>
        <Grid
          rows={rows}
          columns={columns}
          loading={loading}
          columnAutoWidth={true}
          getRowId={(row) => row.id || row.index_column}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          handlePageChange={handlePageChange}
          rowsTotal={rows.length}
          pageCount={pageCount}
          paginationMode="client"
          dosntHaveSearch={true}
          dosntHaveRefresh={true}
        />
      </Box>
    </Box>
  );
}
